import { useMemo, useCallback } from 'react';
import { useTable, useSortBy } from 'react-table';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { useQuery } from '@apollo/client';

import { START_ACTION } from '../../reducers/actionReducer';
import DisableUser from './DisableUser';
import SendPasswordReset from './SendPasswordReset';
import { RATECARDS_QUERY } from '../../queries/ratecards';

import {
  formatDateCellCustom,
  formatDateCellISO,
  formatDateCellShort,
} from '../../helpers/reactTable';
import TableButton from '../inputs/TableButton';
import { wrapInToggleShowVal } from '../../helpers/reactTable';
import EnableUser from './EnableUser';
import EditUserRoles from './EditUserRoles';
import { RateCardSelector } from './RateCardSelector';
import { PaymentTypeSelector } from './PaymentTypeSelector';

export default function UsersTable({
  users,
  usersQuery,
  actionState,
  dispatchAction,
}) {

  // Fetch ratecards from the database
  const { data: ratecardsData, loading: ratecardsLoading } = useQuery(RATECARDS_QUERY);

  // Use the fetched ratecards or an empty array if still loading
  const rateCardOptions = useMemo(() => {
    if (ratecardsLoading || !ratecardsData?.ratecards) {
      return [];
    }
    return ratecardsData.ratecards;
  }, [ratecardsData, ratecardsLoading]);

  const renderUserRolesCell = useCallback(({ row }) => {
    const roles = row.original.roles;

    return (
      <div className="flex items-center space-x-2 whitespace-nowrap">
        {roles?.includes('admin') && (
          <span className="text-white font-bold bg-secondary py-px px-4 rounded">
            Admin
          </span>
        )}
        {roles?.includes('finance-admin') && (
          <span className="text-white font-bold bg-secondary py-px px-4 rounded">
            Finance Admin
          </span>
        )}
        {roles?.includes('super-user') && (
          <span className="text-white font-bold bg-secondary py-px px-4 rounded">
            Super User
          </span>
        )}
        {roles?.includes('super-user-pii') && (
          <span className="text-white font-bold bg-secondary py-px px-4 rounded">
            Super User (PII)
          </span>
        )}
        {roles?.includes('standard-user') && (
          <span className="text-white font-bold bg-secondary py-px px-4 rounded">
            Standard User
          </span>
        )}
        {roles?.includes('view-only') && (
          <span className="text-white font-bold bg-secondary py-px px-4 rounded">
            View Only
          </span>
        )}
        {roles?.includes('activations-user') && (
          <span className="text-white font-bold bg-secondary py-px px-4 rounded">
            Activations
          </span>
        )}
        {roles?.includes('model-files-user') && (
          <span className="text-white font-bold bg-secondary py-px px-4 rounded">
            Alliant Data Sources
          </span>
        )}
      </div>
    );
  }, []);

  const renderUserActionsCell = useCallback(({ value: id, row }) => {
    return (
      <div className="flex items-center space-x-2 whitespace-nowrap">
        {!row.original.disabled && (
          <TableButton
            onClick={() =>
              dispatchAction({
                type: START_ACTION,
                payload: {
                  name: 'Disable User',
                  component: (
                    <DisableUser
                      id={id}
                      usersQuery={usersQuery}
                      dispatchAction={dispatchAction}
                    />
                  ),
                },
              })
            }
            disabled={row.original.disabled}
          >
            Disable User
          </TableButton>
        )}
        {row.original.disabled && (
          <>
            <span className="text-white font-bold bg-gray-500 py-px px-4 rounded">
              Disabled
            </span>
            <TableButton
              onClick={() =>
                dispatchAction({
                  type: START_ACTION,
                  payload: {
                    name: 'Enable User',
                    component: (
                      <EnableUser
                        id={id}
                        usersQuery={usersQuery}
                        dispatchAction={dispatchAction}
                      />
                    ),
                  },
                })
              }
            >
              Enable User
            </TableButton>
          </>
        )}
        <TableButton
          onClick={() =>
            dispatchAction({
              type: START_ACTION,
              payload: {
                name: 'Send Password Reset',
                component: (
                  <SendPasswordReset
                    email={row.values.email}
                    dispatchAction={dispatchAction}
                  />
                ),
              },
            })
          }
          disabled={row.original.disabled}
        >
          Send Password Reset
        </TableButton>
        <TableButton
          onClick={() =>
            dispatchAction({
              type: START_ACTION,
              payload: {
                name: 'Edit Roles',
                component: (
                  <EditUserRoles
                    id={id}
                    row={row}
                    usersQuery={usersQuery}
                    dispatchAction={dispatchAction}
                  />
                ),
              },
            })
          }
          disabled={row.original.disabled}
        >
          Edit Roles
        </TableButton>
      </div>
    );
  }, [dispatchAction, usersQuery]);


  const data = useMemo(() => users, [users]);
  const columns = useMemo(
    () => [
      {
        Header: (info) => {
          const total = useMemo(() => info.rows.length, [info.rows]);
          return <>Total Users: {total}</>;
        },
        id: 'main',
        columns: [
          {
            Header: 'User Role',
            accessor: 'id',
            id: 'userRole', // Unique ID required when multiple columns use the same accessor
            Cell: ({ row }) => renderUserRolesCell({ row }),
          },
          {
            Header: 'Actions',
            accessor: 'id',
            id: 'userActions', // Unique ID required when multiple columns use the same accessor
            Cell: ({ value, row }) => renderUserActionsCell({ value, row }),
          },
          {
            Header: 'Email',
            accessor: 'email',
          },
          {
            Header: 'Plan',
            accessor: 'plan',
          },
        ],
      },
      {
        Header: 'Profile Details',
        id: 'profileDetails',
        columns: [
          {
            Header: 'Avatar',
            accessor: 'avatar',
            Cell: ({ value }) => (
              <div className="mx-auto overflow-hidden rounded-full h-8 w-8 flex items-center justify-center">
                {value ? (
                  <img
                    className="object-contain object-center"
                    src={value}
                    alt="user avatar"
                  />
                ) : (
                  'No avatar'
                )}
              </div>
            ),
          },
          {
            Header: 'Company Name',
            accessor: 'companyName',
          },
          {
            Header: 'First Name',
            accessor: 'firstName',
          },
          {
            Header: 'Last Name',
            accessor: 'lastName',
          },
          {
            Header: 'Phone Number',
            accessor: 'phoneNumber',
          },
          {
            Header: 'Title',
            accessor: 'title',
          },
          {
            Header: 'Industry',
            accessor: 'industry',
          },
          {
            Header: 'Company Size',
            accessor: 'companySize',
          },
          {
            Header: 'Rate Card',
            accessor: 'rateCard',
            id: 'rateCard', // Explicit ID for clarity
            Cell: ({ value, row }) => (
              <RateCardSelector
                currentValue={row.original.rateCard}
                userId={row.original.id}
                rateCardOptions={rateCardOptions}
              />
            )
          },
          {
            Header: 'Payment Type',
            accessor: 'paymentType',
            id: 'paymentType',
            Cell: ({ value, row }) => (
              <PaymentTypeSelector
                currentValue={value}
                userId={row.original.id}
              />
            ),
          },
        ],
      },
      {
        Header: 'Metadata',
        id: 'metadata',
        columns: [
          {
            Header: 'Stripe ID',
            accessor: 'stripeId',
            Cell: wrapInToggleShowVal,
          },
          {
            Header: 'Created On',
            accessor: 'createdAt',
            Cell: ({ value }) =>
              formatDateCellCustom({
                value,
                pattern: 'MM/dd/yy, hh:mm a',
              }),
          },
          {
            Header: 'Last Login',
            accessor: 'lastLogin',
            Cell: ({ value }) =>
              formatDateCellCustom({
                value,
                ifEmpty: "Hasn't logged in",
                pattern: 'MM/dd/yy, hh:mm a',
              }),
          },
          {
            Header: 'Last Accepted Terms of Use',
            accessor: 'termsOfUse.lastAccepted',
            Cell: ({ value }) =>
              formatDateCellCustom({
                value,
                ifEmpty: 'Terms not accepted',
                pattern: 'MM/dd/yy, hh:mm a',
              }),
          },
          {
            Header: 'Last Password Reset',
            accessor: 'passwordReset',
            Cell: ({ value }) =>
              formatDateCellCustom({
                value,
                pattern: 'MM/dd/yy, hh:mm a',
              }),
          },
          {
            Header: 'Reset Token',
            accessor: 'resetToken.token',
            Cell: wrapInToggleShowVal,
          },
          {
            Header: 'Reset Token Expires',
            accessor: 'resetToken.expires',
            Cell: ({ value }) =>
              formatDateCellCustom({
                value,
                pattern: 'MM/dd/yy, hh:mm a',
              }),
          },
        ],
      },
    ],
    [renderUserRolesCell, renderUserActionsCell, rateCardOptions]
  );


  const tableInstance = useTable(
    {
      columns,
      data,
    },
    useSortBy
  );
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <table {...getTableProps()} className="border">
      <thead className="whitespace-nowrap">
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()} className="">
            {headerGroup.headers.map((column) => (
              <th
                {...column.getHeaderProps(column.getSortByToggleProps())}
                className="border text-left hover:bg-gray-100 sticky top-0 bg-white"
              >
                <div
                  className="flex items-center p-2"
                  style={{ borderSpacing: 0 }}
                >
                  {column.render('Header')}
                  {column.isSorted && (
                    <span className="pl-2">
                      {column.isSortedDesc ? <FaArrowDown /> : <FaArrowUp />}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()} className="whitespace-nowrap">
        {rows.map((row) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map((cell) => (
                <td {...cell.getCellProps()} className="border p-2">
                  {cell.render('Cell')}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
